import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useLocation } from 'react-router-dom';
import { Loader2, ShieldCheck, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMfaEnrollmentStart, useMfaEnrollmentConfirm } from '@/api/hooks/useAuth';

// Sprint T — mandatory MFA enrollment page for tenant users.
// Navigated to from LoginPage / AcceptInvitationPage via `useNavigate` with
// `state: { setupToken }`. The setup token is never in the URL (security).

export function MfaEnrollmentPage() {
  const location = useLocation();
  const setupToken: string | undefined = (location.state as any)?.setupToken;

  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [setupData, setSetupData] = useState<{ secret: string; otpauthUrl: string } | null>(null);

  const startMutation = useMfaEnrollmentStart();
  const confirmMutation = useMfaEnrollmentConfirm();

  // Kick off the start call on first mount if we have a token
  useState(() => {
    if (setupToken && !setupData && !startMutation.isPending) {
      startMutation.mutate(setupToken, {
        onSuccess: (data) => setSetupData(data),
        onError: () => toast.error('Failed to load MFA setup — please log in again'),
      });
    }
  });

  function handleCopy() {
    if (!setupData?.secret) return;
    navigator.clipboard.writeText(setupData.secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const totpCode = code.replace(/\s/g, '');
    if (totpCode.length !== 6 || !setupToken) return;
    confirmMutation.mutate({ setupToken, totpCode }, {
      onError: (err: any) => toast.error(err?.response?.data?.errors?.[0]?.message ?? 'Invalid code — please try again'),
    });
  }

  if (!setupToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
        <div className="text-center space-y-2">
          <p className="text-sm font-medium text-accent-rose">Missing enrollment token.</p>
          <p className="text-xs text-text-secondary">Please log in again to continue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,159,253,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,159,253,1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-cyan shadow-lg shadow-accent-cyan/20">
            <ShieldCheck className="h-6 w-6 text-bg-base" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-text-primary">Set Up Two-Factor Auth</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Required before accessing your account</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {startMutation.isPending || !setupData ? (
            <div className="flex items-center justify-center gap-2 py-8 text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Generating setup code…</span>
            </div>
          ) : startMutation.isError ? (
            <div className="py-6 text-center space-y-2">
              <p className="text-sm font-medium text-accent-rose">Failed to load MFA setup.</p>
              <p className="text-xs text-text-secondary">Please log in again to continue.</p>
            </div>
          ) : (
            <>
              <div className="mb-5 flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent-cyan" />
                <div>
                  <h2 className="text-base font-semibold text-text-primary">Scan with your authenticator</h2>
                  <p className="mt-0.5 text-sm text-text-secondary">
                    Use Google Authenticator, Authy, or any TOTP-compatible app.
                  </p>
                </div>
              </div>

              <div className="mb-4 flex justify-center">
                <div className="rounded-lg border border-border-subtle bg-white p-2">
                  {/* QR rendered locally (inline SVG) from otpauthUrl — secret stays in-browser */}
                  <QRCodeSVG value={setupData.otpauthUrl} size={160} marginSize={2} title="MFA QR Code" />
                </div>
              </div>

              <div className="mb-5 space-y-1.5">
                <p className="text-xs text-text-secondary">Can't scan? Enter this key manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded border border-border-subtle bg-bg-surface-2 px-3 py-1.5 text-xs font-mono text-text-primary break-all select-all">
                    {setupData.secret}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="shrink-0 rounded border border-border-subtle bg-bg-surface-2 p-1.5 text-text-secondary hover:text-text-primary"
                    aria-label="Copy secret"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-accent-emerald" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="enrollment-code">Verification code</Label>
                  <Input
                    id="enrollment-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000 000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, ''))}
                    maxLength={7}
                    autoFocus
                    autoComplete="one-time-code"
                    className="text-center tracking-widest text-lg font-mono"
                  />
                  <p className="text-xs text-text-secondary">
                    Enter the 6-digit code shown in your app to confirm setup.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={confirmMutation.isPending || code.replace(/\s/g, '').length < 6}
                >
                  {confirmMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying…</>
                  ) : (
                    'Enable Two-Factor Auth'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
